// frontend/src/pages/ResetPasswordPage.js
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../api'

const font = "'Noto Serif JP', Georgia, serif"
const ink = '#1A0A00'
const muted = '#8A7A5A'
const accent = '#C41E3A'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const token = new URLSearchParams(window.location.search).get('token')

  const [tokenValid, setTokenValid] = useState(null) // null=checking, true, false
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) { setTokenValid(false); return }
    api.get(`/auth/reset-password?token=${encodeURIComponent(token)}`)
      .then(res => setTokenValid(res.data.valid))
      .catch(() => setTokenValid(false))
  }, [token])

  const handleSubmit = async e => {
    e.preventDefault()
    if (password !== confirm) { setError('Passwords do not match'); return }
    setError('')
    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, password })
      setDone(true)
    } catch (err) {
      const msg = err.response?.data?.error
      if (msg === 'invalid_or_expired') {
        setTokenValid(false)
      } else {
        setError(msg || 'Reset failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <Link to="/login" style={s.back}>← Back to sign in</Link>

        <div style={s.sealWrap}>
          <div style={s.seal}><span style={s.sealText}>相撲{'\n'}番付</span></div>
        </div>

        <div style={s.logoArea}>
          <h1 style={s.logoText}>Reset Password</h1>
          <p style={s.tagline}>Sumo Pick'em</p>
        </div>

        {tokenValid === null && (
          <p style={s.checking}>Verifying link…</p>
        )}

        {tokenValid === false && (
          <div>
            <p style={s.invalidMsg}>
              This reset link is invalid or has expired. Please request a new one.
            </p>
            <p style={s.forgotLink}>
              <Link to="/forgot-password" style={s.link}>Request a new reset link →</Link>
            </p>
          </div>
        )}

        {tokenValid === true && !done && (
          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>New password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoFocus
                minLength={8}
                style={s.input}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Confirm new password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                style={s.input}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
            {error && <p style={s.error}>{error}</p>}
            <button type="submit" disabled={loading} style={s.btn}>
              {loading ? 'Saving…' : 'Set new password'}
            </button>
          </form>
        )}

        {done && (
          <div>
            <p style={s.success}>Password updated. You can now sign in.</p>
            <p style={s.forgotLink}>
              <Link to="/login" style={s.link}>Sign in →</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#F5F0E8',
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,160,120,0.06) 3px, rgba(180,160,120,0.06) 4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  card: {
    background: '#FFFFFF', border: '1px solid #D4C8A8', borderRadius: 4,
    padding: '40px 36px', width: '100%', maxWidth: 380,
    boxShadow: '0 4px 24px rgba(42,26,14,0.08)', position: 'relative',
  },
  back: {
    position: 'absolute', top: 18, left: 22,
    color: muted, textDecoration: 'none', fontFamily: font, fontSize: 12, letterSpacing: 1,
  },
  sealWrap: { textAlign: 'center', marginBottom: 12 },
  seal: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 48, height: 48, background: accent, borderRadius: 4,
  },
  sealText: {
    color: '#F5F0E8', fontSize: 9, fontWeight: 700, textAlign: 'center',
    lineHeight: 1.3, whiteSpace: 'pre', fontFamily: font,
  },
  logoArea: { textAlign: 'center', marginBottom: 28 },
  logoText: { fontFamily: font, fontSize: 22, fontWeight: 900, color: ink, margin: '0 0 4px' },
  tagline: { color: muted, fontFamily: font, fontSize: 12, margin: 0, letterSpacing: 1 },
  checking: { color: muted, fontFamily: font, fontSize: 13, textAlign: 'center' },
  invalidMsg: {
    color: accent, fontFamily: font, fontSize: 13, lineHeight: 1.6,
    background: '#FEF0F0', border: '1px solid #D4A8A8',
    borderRadius: 3, padding: '14px 16px', margin: '0 0 14px',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 10, color: muted, fontFamily: font, letterSpacing: 1, textTransform: 'uppercase' },
  input: {
    padding: '10px 12px', background: '#FDFBF7',
    border: '1px solid #D4C8A8', borderRadius: 2,
    color: ink, fontSize: 14, fontFamily: font, outline: 'none',
    width: '100%', boxSizing: 'border-box',
  },
  error: { color: accent, fontFamily: font, fontSize: 12, margin: 0 },
  btn: {
    padding: '12px', background: accent, color: '#F5F0E8',
    border: 'none', borderRadius: 2, fontSize: 11, fontWeight: 700,
    fontFamily: font, cursor: 'pointer', letterSpacing: 3, textTransform: 'uppercase',
    marginTop: 4,
  },
  success: {
    color: '#1A6A1A', fontFamily: font, fontSize: 13, lineHeight: 1.6,
    background: '#F0FAF0', border: '1px solid #A8D4A8',
    borderRadius: 3, padding: '14px 16px', margin: '0 0 14px',
  },
  forgotLink: { textAlign: 'center', margin: 0, fontFamily: font, fontSize: 12 },
  link: { color: accent, textDecoration: 'none' },
}
